import { closestCenter, DndContext } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { FormProvider } from 'react-hook-form'
import { LuCirclePlus, LuInfo } from 'react-icons/lu'
import { useTheme } from 'styled-components'

import { useGetUser } from '@/feature/auth/api/useGetUser'

import { Button, IconButton } from '@/lib/core/components/Button'
import { Modal } from '@/lib/core/components/Modal'
import { H3 } from '@/lib/core/components/Text'
import { TextArea } from '@/lib/core/components/TextArea'
import { TextField } from '@/lib/core/components/TextField'
import { Label } from '@/lib/core/components/TextField/TextField.styles'
import { Tooltip } from '@/lib/core/components/Tooltip'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'

import styles from './CreateTaskModal.module.css'
import { CreateTaskModalProps, TaskStatus } from './CreateTaskModal.types'
import { SortableLocationRow } from '../SortableLocationRow'
import { useCreateTaskForm } from './useCreateTaskForm'

const FieldTooltip = ({
  title,
  firstParagraph,
  secondParagraph,
}: {
  title: string
  firstParagraph: string
  secondParagraph: string
}) => {
  const { colors } = useTheme()

  return (
    <div className={styles.tooltipWrapper}>
      <Tooltip
        interactive
        trigger="mouseenter"
        tooltipContent={
          <div>
            <label className={styles.tooltipTitle}>{title}</label>
            <p>{firstParagraph}</p>
            <p>{secondParagraph}</p>
          </div>
        }
        contentPadding={2}
      >
        <IconButton type="button" $variant="primary" className={styles.tooltipButton}>
          <LuInfo color={colors.active} />
        </IconButton>
      </Tooltip>
    </div>
  )
}

export const CreateTaskModal = ({
  isActive,
  onClose,
  onTaskCreated,
  initialData,
}: CreateTaskModalProps) => {
  const { data: user } = useGetUser()

  const {
    formMethods,
    formError,
    isMutating,
    stops,
    stopIds,
    stopErrors,
    handleSubmit,
    handleDragEnd,
    handleLocationChange,
    handleDatetimeChange,
    handleAddStop,
    handleRemoveStop,
    handleCancel,
    onSubmit,
  } = useCreateTaskForm({ isActive, initialData, onClose, onTaskCreated })

  return (
    <Modal
      containerClassName={styles.taskModalContainer}
      isActive={isActive}
      onEscape={handleCancel}
      actions={
        <>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="large"
            disabled={isMutating}
            data-testid="create-task-modal-cancel"
            style={{ backgroundColor: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="secondary"
            size="large"
            disabled={isMutating}
            isLoading={isMutating}
            data-testid="create-task-modal-submit"
            type="submit"
          >
            Save
          </Button>
        </>
      }
      actionsSticky={true}
      dataTestId="create-task-modal"
    >
      <H3>Create New Task</H3>
      {formError && <div className={styles.errorMessage}>{formError}</div>}
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.createTaskForm}>
          <div className={styles.formRow}>
            <div className={styles.formTextDisplay}>
              <Label $displayInline={true}>Organisation Issued: </Label>
              <div className={styles.organizationText}>
                <CircleAvatar src={user?.organization?.avatar} size={24} withBorder />
                {user?.organization?.name ?? '-'}
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <FieldTooltip
                title="Task Reference"
                firstParagraph="A unique identifier you give the task to track it easily."
                secondParagraph="Example: You can use a PO number or job ID as Reference, or give your task a name. Enter it here to link the task back to the request. This makes searching and reporting easier later on."
              />
              <TextField
                name="reference"
                label="Task Reference"
                placeholder="Enter task reference"
                required
              />
            </div>
            <div className={styles.formField}>
              <FieldTooltip
                title="Status"
                firstParagraph="The task status will update automatically as the task progresses."
                secondParagraph="Example: When a vehicle is assigned, en route, or completed, the status changes on its own — no manual updates are needed."
              />
              <TextField
                name="statusDisplay"
                label="Status"
                value={TaskStatus.REQUESTED}
                disabled
                readOnly
              />
            </div>
          </div>

          <div className={styles.formField}>
            <FieldTooltip
              title="Description"
              firstParagraph="Add key details about the task so everyone knows what it involves."
              secondParagraph={'Example: You could describe the load as "Coal delivery to Richards Bay" or "Pickup of construction materials".'}
            />
            <TextArea
              name="description"
              label="Description"
              placeholder="Enter task description..."
              rows={4}
              className={styles.taskFormTextArea}
            />
          </div>
        </form>
      </FormProvider>

      <h3 className={styles.setLocationsHeader}>Set Locations</h3>

      <div className={styles.locationsContainer}>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
            {stops.map((stop, index) => (
              <SortableLocationRow
                key={stop.id}
                stop={stop}
                index={index}
                total={stops.length}
                canRemove={stops.length > 2}
                onLocationChange={handleLocationChange}
                onDatetimeChange={handleDatetimeChange}
                onRemove={handleRemoveStop}
                locationError={stopErrors.get(stop.id)?.location}
                locationWarning={stopErrors.get(stop.id)?.locationWarning}
                datetimeError={stopErrors.get(stop.id)?.datetime}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button
          type="button"
          className={styles.addStopButton}
          onClick={handleAddStop}
          data-testid="add-stop-button"
        >
          <LuCirclePlus size={16} />
          Add another
        </button>
      </div>
    </Modal>
  )
}
